import React, { useState, useRef, useEffect } from 'react';
import api from '../api';
import {
  Box, Paper, Typography, Avatar, TextField, IconButton, InputAdornment,
  List, ListItem, ListItemText, Dialog, Button, Badge
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import CallIcon from '@mui/icons-material/Call';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import DescriptionIcon from '@mui/icons-material/Description';
import ClipboardIcon from '@mui/icons-material/ContentCopy';
import VideoCallIcon from '@mui/icons-material/VideoCall';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import CustomLocationLoader from '../customcomponents/CustomLocationLoader';
import CustomAudioPlayer from '../customcomponents/CustomAudioPlayer';
import CustomVideoPlayer from '../customcomponents/CustomVideoPlayer';
import { ArrowBack } from '@mui/icons-material';

export default function DoctorChatViewPage() {
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesError, setMessagesError] = useState(null);
  const [chatList, setChatList] = useState([]);
  const [chatListLoading, setChatListLoading] = useState(false);
  const [chatListError, setChatListError] = useState(null);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [selectedChatId, setSelectedChatId] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showChat, setShowChat] = useState(false);

  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const textColor = isDark ? theme.palette.background.text : theme.palette.text.secondary;

  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const messagesEndRef = useRef(null);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Fetch chat list
  useEffect(() => {
    setChatListLoading(true);
    setChatListError(null);
    api.get('/tickets/assigned-to-me', { params: { page: 1, limit: 20 } })
      .then(res => {
        if (res.data?.success && Array.isArray(res.data.data)) {
          const mapped = res.data.data.map(ticket => ({
            id: ticket._id,
            name: ticket.patient?.name || 'No Name',
            lastMessage: ticket.description || '-',
            time: ticket.updatedAt ? new Date(ticket.updatedAt).toLocaleDateString() : '',
            avatar: (ticket.patient?.name || 'T')[0].toUpperCase(),
            raw: ticket,
            // unread: Math.random() > 0.7 ? Math.floor(Math.random() * 5) + 1 : 0,
          })).filter(ticket => ticket.raw.status === 1);
          setChatList(mapped);
          setSelectedChatId(null);
        } else {
          setChatList([]);
        }
      })
      .catch(() => {
        setChatListError('Failed to load chats');
        setChatList([]);
      })
      .finally(() => setChatListLoading(false));
  }, []);

  // Filtered chat list
  const filteredChats = chatList.filter(chat =>
    chat.name.toLowerCase().includes(search.toLowerCase()) ||
    chat.lastMessage.toLowerCase().includes(search.toLowerCase())
  );

  // Fetch messages
  useEffect(() => {
    if (!selectedChatId) {
      setMessages([]);
      return;
    }
    setMessagesLoading(true);
    setMessagesError(null);
    api.get(`/tickets/${selectedChatId}/my-threads`, { params: { page: 1, limit: 50 } })
      .then(res => {
        console.log('res',res.data);
        
        if (res.data?.success && Array.isArray(res.data.data)) {
          const currentUserId = localStorage.getItem('userId');
          const currentUserEmail = localStorage.getItem('userEmail');
          const currentUserName = localStorage.getItem('name');
          const mapped = res.data.data.map(thread => {
            const createdBy = thread.createdBy || thread.TA_Created_By;
            const threadType = thread.threadType || thread.TA_Thread_Type;
            const isFromCurrentDoctor = threadType=='system';

            return {
              id: thread._id,
              message: thread.message || thread.TA_Thread_Message,
              name: isFromCurrentDoctor ? 'Doctor' : createdBy?.name || createdBy?.TA_User_Name || 'Patient',
              timestamp: thread.createdAt
                ? new Date(thread.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                : '',
              isFromDoctor: isFromCurrentDoctor,
              rawData: thread.rawData || thread.TA_Raw_Data || null, 
              threadType: threadType,
              createdBy:createdBy
            };
          }).reverse();
          setMessages(mapped);
        } else {
          setMessages([]);
        }
      })
      .catch(() => {
        setMessagesError('Failed to load messages');
        setMessages([]);
      })
      .finally(() => setMessagesLoading(false));
  }, [selectedChatId]);

  const handleSend = async () => {
    if (!input.trim() || !selectedChatId) return;
    setSending(true);
    try {
      await api.post(`/tickets/${selectedChatId}/threads/message`, {
        message: input,
        isInternal: false,
        attachments: [],
        isExpertMessage:true
      });
      setInput('');
      // Refresh messages
      const res = await api.get(`/tickets/${selectedChatId}/my-threads`, { params: { page: 1, limit: 50 } });
      // const currentUserId = localStorage.getItem('userId');
      // const currentUserEmail = localStorage.getItem('userEmail');
      // const currentUserName = localStorage.getItem('name');
      if (res.data?.success) {
        const mapped = res.data.data.map(thread => {
          const createdBy = thread.createdBy || thread.TA_Created_By;
          const threadType = thread.threadType || thread.TA_Thread_Type;
          const isFromCurrentDoctor = threadType=='system';
          return {
            id: thread._id,
            message: thread.message || thread.TA_Thread_Message,
            // name: isFromCurrentDoctor ? 'Doctor' : 'Patient',
            timestamp: thread.createdAt
              ? new Date(thread.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
              : '',
            isFromDoctor: isFromCurrentDoctor,
          };
        }).reverse();
        setMessages(mapped);
      }
    } catch { }
    finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };
  const handleEscape = (e) => {
    if (e.key === 'Escape') {
      setSelectedChatId(null);
    }
  };

  // Escape key press use effect
    useEffect(() => {
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, []);

  const chatBarSx = {
    p: { xs: 1.2, sm: 2 },
    height: { xs: 62, sm: 70 },
    display: 'flex',
    alignItems: 'center',
    gap: 2,
  };

  const currentChat = filteredChats.find(c => c.id === selectedChatId) || chatList.find(c => c.id === selectedChatId);

  // Helper function to determine message type
const getMessageType = (message, rawData) => {
  try {    
    // Check if rawData has contentType
    if (rawData?.data?.message?.message_content_type) {
      return rawData?.data?.message?.message_content_type.toLowerCase();
    }
    
    // Fallback: check if message is JSON location
    const parsed = JSON.parse(message);
    if (parsed && typeof parsed === 'object') {
      if ('address' in parsed || ('latitude' in parsed && 'longitude' in parsed)) {
        return 'location';
      }
      if (Array.isArray(parsed) && parsed[0]?.phones) {
        return 'contact';
      }
    }
  } catch {
    // Not JSON, treat as text
  }
  return 'text';
};

// Helper function to render different message types
const renderMessageContent = (message, rawData,threadType) => {
  const messageType = getMessageType(message, rawData);
  
  switch (messageType) {
    case 'location':
      return renderLocationMessage(message);
    
    case 'image':
      return renderImageMessage(message, rawData);
    
    case 'video':
      return renderVideoMessage(message, rawData);
    
    case 'audio':
      return renderAudioMessage(message, rawData);
    
    case 'document':
      return renderDocumentMessage(message, rawData);
    
    case 'contact':
      return renderContactMessage(message);

    case 'interactivebuttonreply':
      return renderTemplateMessageReply(message);
    
    default:
      return renderTextMessage(message,threadType);
  }
};

// Location message renderer (your existing function)
const renderLocationMessage = (message) => {
  try {
    const locationObj = JSON.parse(message);
    const location = {
      address :locationObj?.address,
      latitude :locationObj?.latitude,
      longitude :locationObj?.longitude,

      name :locationObj?.name,
      url :locationObj?.url,
    };
    
    return (
      <Box
      sx={{
        width: {
          xs: '100%',    // 100% width on extra-small screens
          sm: '350px',   // 350px on small screens
          md: '450px',   // 450px on medium screens
          lg: '500px',   // 500px on large screens and up
        },
        maxWidth: '100%',
      }}
    >
      <CustomLocationLoader data={location} />

      <Box marginTop={2} >
        <Typography variant="h5" sx={{ color: '#cc6d6d' }} >
          {location.name}
        </Typography>
        <Typography sx={{ fontSize:'0.9rem',color: '#888' }}>
          {location.address}
        </Typography>
        <a href={location.url} target="_blank" rel="noopener noreferrer"> {location.url}</a>
      </Box>
    </Box>
    );
  } catch {
    return <Typography variant="body1">{message}</Typography>;
  }
};

// Image message renderer
const renderImageMessage = (message, rawData) => {
  const imgUrl = message?.metadata?.data?.message?.media_url || rawData?.data?.message?.media_url;
  return (
    <img
      src={imgUrl}
      alt="Image message"
      style={{
        maxWidth: '100%',
        maxHeight: '400px',
        borderRadius: 8,
        marginBottom: 8,
        display: 'block', 
      }}
    />
  );
};

// Video message renderer
const renderVideoMessage = (message, rawData) => {
  const videoUrl = message?.metadata?.data?.message?.media_url || rawData?.data?.message?.media_url;
  return (
    <Box sx={{ width: {xs: '100%', sm: '350px',md: '450px',lg: '500px',}, maxWidth: '100%', }} >
      <CustomVideoPlayer src={videoUrl} accentColor="#3b82f6" />
    </Box>
  );
};

// Audio message renderer
const renderAudioMessage = (message, rawData) => {
  const audioUrl = message?.metadata?.data?.message?.media_url || rawData?.data?.message?.media_url;
  return (
    <CustomAudioPlayer src={audioUrl} width={'100%'} />
  );
};

// Document message renderer
const renderDocumentMessage = (message, rawData) => {
  const fileName = rawData?.fileName || 'Document';
  const fileSize = rawData?.fileSize ? `(${formatFileSize(rawData.fileSize)})` : '';
  
  return (
    <Box sx={{ fontFamily: 'inherit' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <DescriptionIcon fontSize="small" color="primary" />
        <Typography variant="subtitle2" fontWeight={600}>
          Document Shared
        </Typography>
      </Box>
      
      <Button
        variant="outlined"
        startIcon={<AttachFileIcon />}
        href={rawData.data?.message?.media_url}
        target="_blank"
        rel="noopener noreferrer"
        sx={{ 
          textTransform: 'none',
          justifyContent: 'flex-start',
          textAlign: 'left'
        }}
        fullWidth
      >
        <Box sx={{ textAlign: 'left' }}>
          <Typography variant="body2" fontWeight={500} noWrap>
            {fileName}
          </Typography>
          {fileSize && (
            <Typography variant="caption" color="text.secondary">
              {fileSize}
            </Typography>
          )}
        </Box>
      </Button>
      
      {rawData?.caption && (
        <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
          {rawData.caption}
        </Typography>
      )}
    </Box>
  );
};

// Text message renderer
const renderTextMessage = (message,threadType) => {  
  const isLongMessage=message.length>100;
  const displayedMessage = isExpanded ? message : `${message.slice(0, 60)}...`;
  const isUserThread= threadType==='user';
  
   return (
    <div>
      <Typography variant="body1" sx={{ lineHeight: 1.4, whiteSpace: 'pre-wrap',fontSize:isSmallScreen?12:14 }}>
        {isLongMessage ? displayedMessage : message}
      </Typography>
      {isLongMessage && (
        <Box sx={{display:'flex',justifyContent: isUserThread ? 'flex-start' : 'flex-end' }}>
          <Button 
            size="small" 
            sx={{ mt: 1, textTransform: 'none', p: 0, minWidth: 0, color: isUserThread ? 'primary.main' : 'white' }} 
            onClick={() => setIsExpanded(prev => !prev)}
          >
            {isExpanded ? 'Read less' : 'Read more'}
          </Button>
        </Box>
      )}
    </div>
  );
};

const renderContactMessage = (message) => {
  try {
    const contacts = JSON.parse(message);

    if (!Array.isArray(contacts)) {
      return renderTextMessage(message);
    }

    return (
      <Box sx={{ fontFamily: 'inherit' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <ContactPhoneIcon fontSize="small" color="primary" />
          <Typography variant="subtitle2" fontWeight={600}>
            Contact Shared
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {contacts.map((contact, index) => (
            <Box
              key={index}
              display="flex"
              alignItems="center"
              sx={{
                background: 'action.hover',
                borderRadius: 2,
                p: 1.5,
                gap: 1.5,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  background: 'action.selected',
                  borderColor: 'primary.main',
                },
              }}
              onClick={() => {
                const phoneNumber = contact?.phones?.[0]?.wa_id || contact?.phones?.[0]?.phone;
                if (phoneNumber && navigator?.clipboard?.writeText) {
                  navigator.clipboard.writeText(phoneNumber)
                    .then(() => {
                      alert('Phone number copied to clipboard');
                    })
                    .catch((err) => {
                      console.error('Failed to copy: ', err);
                    });
                }
              }}
            >
              <Avatar src={contact?.image_url} alt={contact?.name?.formatted_name} sx={{ width: 32, height: 32 }} />

              <Box display="flex" flexDirection="column" sx={{ flex: 1 }}>
                <Typography variant="subtitle2" fontWeight={600} color="text.primary">
                  {contact?.name?.formatted_name || contact?.name || 'Unknown Contact'}
                </Typography>

                {contact?.phones?.map((phone, phoneIndex) => (
                  <Typography key={phoneIndex} variant="body2" color="text.secondary">
                    {phone.phone}
                    {phone.wa_id && ` (${phone.wa_id})`}
                  </Typography>
                ))}

                {contact?.emails?.[0]?.email && (
                  <Typography variant="caption" color="text.secondary">
                    {contact.emails[0].email}
                  </Typography>
                )}
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                <Typography variant="caption" color="primary.main" sx={{ fontWeight: 600 }}>
                  <ClipboardIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    );
  } catch (error) {
    console.error('Error parsing contact message:', error);
  }

  return renderTextMessage(message);
};

const renderTemplateMessageReply= (message) =>{
  try {
    const parsed = JSON.parse(message);
    const title = parsed?.button_reply?.title || parsed?.list_reply?.title || message?.message ||'Unknown Action';
    if (!title) {
      return (
        <Typography
          variant="body1"
          sx={{
            lineHeight: 1.4,
            whiteSpace: "pre-wrap",
            fontSize: isSmallScreen ? 12 : 14
          }}
        >
          {message}
        </Typography>
      );
    }
    return (
      <>
        <Typography variant="body1" sx={{ lineHeight: 1.4, whiteSpace: 'pre-wrap',fontSize:isSmallScreen?12:14 }}>
          {title}
        </Typography>
      </>
    );
  } catch (e) {
    // console.error('Invalid JSON:', e);
    return (
      <Typography
        variant="body1"
        sx={{
          lineHeight: 1.4,
          whiteSpace: "pre-wrap",
          fontSize: isSmallScreen ? 12 : 14
        }}
      >
        {message}
      </Typography>
    );
  }
                                      
}

// Helper function to format file size
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

  return (
    <Box sx={{
      position: 'fixed',
      top: 64,
      left: 0,
      width: '100vw',
      height: 'calc(100vh - 64px)',
      bgcolor: 'background.default',
      display: 'flex',
      justifyContent: 'center',
      zIndex: 1200,
      overflow: 'hidden',
      p: 1
    }}>
      <Box sx={{
        display: 'flex',
        width: '100%',
        height: isSmallScreen?'94%':'100%',
        maxWidth: { xs: '100%', sm: "95%" },
        borderRadius: 4,
        background: 'transparent',
        position: 'relative',
        gap: 2,
        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
      }}>
        {/* Left Panel - Chat List */}
        {(!isSmallScreen || !showChat) && (
          <Paper sx={{
            width: { xs: '100%', sm: 400 },
            height: '100%',
            display: 'flex',
            borderRadius: 3,
            flexDirection: 'column',
            bgcolor: 'background.paper',
            boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
            overflow: 'hidden'
          }}>
            {/* Header */}
            <Box sx={{
              p: 1.4,
              background: 'linear-gradient(135deg, #2193b0 0%, #6dd5ed 100%)',
              color: 'white',
              borderRadius: '12px 12px 0 0'
            }}>
              <Typography variant="h6" fontWeight={600} color={'white'}>
                 Active Chats
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }} color={'white'}>
                {chatList.length} chats
              </Typography>
            </Box>

            {/* Search */}
            <Box sx={{ p: 2, pb: 1 }}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                placeholder="Search patients..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    backgroundColor: 'background.default',
                    color:'background.text'
                  }
                }}
              />
            </Box>

            {/* Chat List */}
            <Box sx={{ flex: 1, overflowY: 'auto', p: 1 }}>
              {filteredChats.map(chat => (
                <ListItem
                  key={chat.id}
                  button
                  selected={chat.id === selectedChatId}
                  onClick={() => {
                    setSelectedChatId(chat.id);
                    if (isSmallScreen) setShowChat(true);
                  }}
                  sx={{
                    borderRadius: 2,
                    mb: 1,
                    py: 1.2,
                    border: '1px solid',
                    borderColor: chat.id === selectedChatId ? 'primary.main' : 'transparent',
                    backgroundColor: chat.id === selectedChatId ? 'primary.light' : 'transparent',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    }
                  }}
                >
                  <Badge
                    color="error"
                    badgeContent={chat.unread}
                    invisible={!chat.unread}
                    sx={{ mr: 2 }}
                  >
                    <Avatar 
                      sx={{ 
                        width: isSmallScreen?42:48, 
                        height: isSmallScreen?42:48, 
                        bgcolor: chat.id === selectedChatId ? 'white' : 'primary.main',
                        color: chat.id === selectedChatId ? 'primary.main' : 'white',
                        fontWeight: 600,
                        fontSize: '1.1rem'
                      }}
                    >
                      {chat.avatar}
                    </Avatar>
                  </Badge>
                  <Box sx={{ flex: 1, minWidth: 0 ,ml:-1}}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Typography variant="subtitle1" fontWeight={600} noWrap color={'background.text'} textTransform={'capitalize'} sx={{fontSize: isSmallScreen ? 14 : 16 }} >
                        {chat.name}
                      </Typography>
                      <Typography variant="caption" color={'background.text'} sx={{fontSize: isSmallScreen ? 10 : 12 }}>
                        {chat.time}
                      </Typography>
                    </Box>
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      noWrap
                      sx={{ 
                        fontWeight: chat.unread ? 600 : 400,
                        color: chat.unread ? 'text.primary' : 'text.secondary',
                        fontSize: isSmallScreen ? 12 : 14 
                      }}
                    >
                      {chat.lastMessage}
                    </Typography>
                  </Box>
                </ListItem>
              ))}
              {filteredChats.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography color={'background.text'}sx={{fontSize:isSmallScreen?14:16}} >
                    No conversations found
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>
        )}

        {/* Right Panel - Chat Area */}
        {(!isSmallScreen || showChat) && (
          <Paper sx={{
            flex: 1,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 3,
            overflow: 'hidden',
            bgcolor: 'background.paper',
            boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)'
          }}>
            {selectedChatId ? (
              <>
                {/* Chat Header */}
                <Box sx={{
                  p: 2,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  background: 'linear-gradient(135deg, rgba(173,216,230,0.25), rgba(255,255,255,0.15))',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {isSmallScreen && (
                      <IconButton onClick={() => setShowChat(false)} size="small">
                        <ArrowBack />
                      </IconButton>
                    )}
                    <Avatar 
                      sx={{ 
                        width: isSmallScreen?42:48, 
                        height:isSmallScreen?42: 48, 
                        bgcolor: 'primary.main',
                        color: 'white',
                        fontWeight: 600
                      }}
                    >
                      {currentChat?.avatar}
                    </Avatar>
                    <Box>
                      <Typography variant="h6" fontWeight={600} color={'background.text'} textTransform={'capitalize'} sx={{fontSize:isSmallScreen?16:18}}>
                        {currentChat?.name}
                      </Typography>
                      {/* <Typography variant="body2" color="success.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main' }} />
                        Online
                      </Typography> */}
                    </Box>
                  </Box>
                  {/* <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton sx={{ color: 'primary.main' }}>
                      <CallIcon />
                    </IconButton>
                    <IconButton sx={{ color: 'primary.main' }}>
                      <VideoCallIcon />
                    </IconButton>
                    <IconButton sx={{ color: 'primary.main' }}>
                      <MoreVertIcon />
                    </IconButton>
                  </Box> */}
                </Box>

                {/* Messages Area */}
                <Box sx={{
                  flex: 1,
                  overflowY: 'auto',
                  p: 2,
                  background: theme.palette.background.gradient,
                  '&::-webkit-scrollbar': {
                    width: '6px',
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: 'transparent',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'rgba(0,0,0,0.2)',
                    borderRadius: '3px',
                  },
                }}>
                  {messages.map((msg, index) => (
                    <Box
                      key={msg.id}
                      sx={{
                        display: 'flex',
                        justifyContent: msg.isFromDoctor ? 'flex-end' : 'flex-start',
                        mb: 2,
                        animation: 'fadeIn 0.3s ease-in',
                      }}
                    >
                      <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        maxWidth: '70%',
                        alignItems: msg.isFromDoctor ? 'flex-end' : 'flex-start'
                      }}>
                        {msg?.createdBy?.role?.name == 'Admin' && (
                          <Typography variant="caption" color={'background.text'} sx={{ ml: 1, mb: 0.5 }}>
                            {msg?.createdBy?.role?.name}
                          </Typography>
                        )}
                        {/* chat bubble */}
                        <Paper
                          sx={{
                            px: 2,
                            py: 1.5,
                            borderRadius: 3,
                            bgcolor: msg.isFromDoctor ? 'primary.main' : 'white',
                            color: msg.isFromDoctor ? 'white' : 'text.primary',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            border: msg.isFromDoctor ? 'none' : '1px solid',
                            borderColor: 'divider',
                            position: 'relative',
                            '&::before': msg.isFromDoctor ? {
                              content: '""',
                              position: 'absolute',
                              right: -8,
                              top: 12,
                              width: 0,
                              height: 0,
                              borderLeft: '8px solid',
                              borderLeftColor: 'primary.main',
                              borderTop: '8px solid transparent',
                              borderBottom: '8px solid transparent'
                            } : {
                              content: '""',
                              position: 'absolute',
                              left: -8,
                              top: 12,
                              width: 0,
                              height: 0,
                              borderRight: '8px solid',
                              borderRightColor: 'white',
                              borderTop: '8px solid transparent',
                              borderBottom: '8px solid transparent'
                            }
                          }}
                          onClick={()=>console.log(msg)}
                        >
                          {renderMessageContent(msg.message, msg.rawData,msg.threadType)}
                        </Paper>
                        <Typography variant="caption" color={'background.text'} sx={{ mt: 0.5, mx: 1 }}>
                          {msg.timestamp}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                  <div ref={messagesEndRef} />
                </Box>

                {/* Message Input */}
                <Box sx={{
                  p: 2,
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.paper'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
                    {/* <IconButton size="small">
                      <AttachFileIcon />
                    </IconButton>
                    <IconButton size="small">
                      <EmojiEmotionsIcon />
                    </IconButton> */}
                    <TextField
                      fullWidth
                      multiline
                      maxRows={4}
                      placeholder="Type your message..."
                      variant="outlined"
                      size="small"
                      value={input}
                      onChange={e => setInput(e.target.value)}
                      onKeyDown={handleKeyDown}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 3,
                          color:'background.text'
                        },
                      }}
                    />
                    <IconButton 
                      onClick={handleSend} 
                      disabled={sending || !input.trim()}
                      sx={{
                        bgcolor: 'primary.main',
                        color: 'white',
                        '&:hover': {
                          bgcolor: 'primary.dark',
                        },
                        '&:disabled': {
                          bgcolor: 'action.disabled',
                        }
                      }}
                    >
                      <SendIcon />
                    </IconButton>
                  </Box>
                </Box>
              </>
            ) : (
              // No chat selected state
              <Box sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'background.default',
                p: 3
              }}>
                <Box sx={{
                  width: 120,
                  height: 120,
                  borderRadius: '50%',
                  bgcolor: 'primary.light',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 3
                }}>
                  <CallIcon sx={{ fontSize: 48, color: 'primary.main' }} />
                </Box>
                <Typography variant="h5" fontWeight={600} color={'background.text'} gutterBottom>
                  Welcome to Patient Chat
                </Typography>
                <Typography variant="body1" color={textColor} textAlign="center" sx={{ maxWidth: 400 }}>
                  Select a conversation from the list to start chatting with your patients. 
                </Typography>
              </Box>
            )}
          </Paper>
        )}
      </Box>

      {/* Patient Profile Dialog */}
      {/* <Dialog 
        open={profileOpen} 
        onClose={() => setProfileOpen(false)} 
        maxWidth="xs" 
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: 3, 
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
          }
        }}
      >
        <Box sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          p: 4,
          textAlign: 'center',
          color: 'white',
          position: 'relative'
        }}>
          <IconButton
            onClick={() => setProfileOpen(false)}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              color: 'white',
              bgcolor: 'rgba(255,255,255,0.2)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
            }}
          >
            <CloseIcon />
          </IconButton>
          <Avatar sx={{ 
            width: 80, 
            height: 80, 
            bgcolor: 'white', 
            color: 'primary.main',
            fontSize: '2rem',
            fontWeight: 600,
            mx: 'auto',
            mb: 2
          }}>
            {currentChat?.avatar}
          </Avatar>
          <Typography variant="h5" fontWeight={600}>
            {currentChat?.name}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            Patient
          </Typography>
        </Box>
        
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Contact Information
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CallIcon color="primary" />
              <Typography>+1 (555) 123-4567</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <VideoCallIcon color="primary" />
              <Typography>Start Video Call</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <EmailIcon color="primary" />
              <Typography>patient@example.com</Typography>
            </Box>
          </Box>
        </Box>
      </Dialog> */}
    </Box>
  );
}